import React, { useEffect, useState } from 'react';
import { animateScroll } from 'react-scroll';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { FiMenu, FiX, FiChevronDown, FiFlag, FiShield, FiTool, FiTarget, FiArrowRight } from 'react-icons/fi';

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [showServicesDropdown, setShowServicesDropdown] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        window.addEventListener('scroll', onScroll);
        onScroll();
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const scrollTo = (section) => {
        if (location.pathname !== '/') {
            navigate('/');
            setTimeout(() => scrollToSection(section), 300);
        } else {
            scrollToSection(section);
        }
    };

    const scrollToSection = (section) => {
        animateScroll.scrollTo((document.getElementById(section)?.offsetTop || 40) - 20, { duration: 500 });
    };

    return (
        <nav
            className={`sticky top-0 z-50 transition-all duration-300 ${
                scrolled
                    ? 'bg-[#05060a]/80 backdrop-blur-md border-b border-white/10'
                    : 'bg-transparent border-b border-transparent'
            } text-white`}
        >
            <div className="px-6 md:px-16 py-3 flex justify-between items-center max-w-7xl mx-auto">
                <RouterLink to="/" className="flex items-center gap-2">
                    <img src="/logo.png" alt="SiberLab Logo" className="h-10 w-auto" />
                </RouterLink>

                <ul className="hidden md:flex gap-1 font-medium items-center">
                    <NavItem onClick={() => scrollTo('home')}>Home</NavItem>

                    <li
                        className="relative"
                        onMouseEnter={() => setShowServicesDropdown(true)}
                        onMouseLeave={() => setShowServicesDropdown(false)}
                    >
                        <span className="cursor-pointer px-3 py-2 rounded-md hover:bg-white/5 hover:text-red-400 flex items-center gap-1 transition">
                            Services <FiChevronDown />
                        </span>
                        {showServicesDropdown && (
                            <div className="absolute right-0 top-full pt-2 w-80">
                                <ul className="bg-[#0a0c14]/95 backdrop-blur-md border border-white/10 rounded-xl shadow-2xl overflow-hidden">
                                    <DropdownItem to="/services/cybersecurity-training" icon={<FiTool />} label="Cybersecurity Training" desc="Hands-on labs & CTF workshops" />
                                    <DropdownItem to="/services/red-team" icon={<FiShield />} label="Red Team & Pentest" desc="Adversary emulation & VAPT" />
                                    <DropdownItem to="/services/ctf-vendor" icon={<FiFlag />} label="CTF Organizer" desc="End-to-end CTF event ops" />
                                    <DropdownItem to="/services/bug-bounty" icon={<FiTarget />} label="Bug Bounty as a Service" desc="Managed vulnerability program" />
                                </ul>
                            </div>
                        )}
                    </li>

                    <NavItem onClick={() => scrollTo('testimonials')}>Testimonials</NavItem>
                    <NavItem onClick={() => scrollTo('contact')}>Contact</NavItem>
                    <li>
                        <a href="https://akademi.siberlab.id/" target="_blank" rel="noreferrer" className="cursor-pointer px-3 py-2 rounded-md hover:bg-white/5 hover:text-red-400 transition inline-block">
                            Akademi Siber
                        </a>
                    </li>

                    <RouterLink
                        to="/services/ctf-vendor"
                        className="ml-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white font-semibold text-sm transition shadow-lg shadow-red-900/30"
                    >
                        Run a CTF <FiArrowRight />
                    </RouterLink>
                </ul>

                <button onClick={() => setIsOpen(!isOpen)} className="md:hidden text-2xl">
                    {isOpen ? <FiX /> : <FiMenu />}
                </button>
            </div>

            {isOpen && (
                <div className="md:hidden bg-[#05060a]/95 backdrop-blur-md border-t border-white/10">
                    <ul className="px-6 py-4 flex flex-col gap-3">
                        <li>
                            <RouterLink to="/" onClick={() => setIsOpen(false)} className="block py-1 hover:text-red-400">
                                Home
                            </RouterLink>
                        </li>
                        <li>
                            <span className="text-gray-300 font-mono text-xs uppercase tracking-wider">Services</span>
                            <ul className="ml-2 mt-2 border-l border-white/10 pl-4 text-sm flex flex-col gap-2">
                                <li><RouterLink to="/services/cybersecurity-training" onClick={() => setIsOpen(false)} className="block py-1 hover:text-red-400">Cybersecurity Training</RouterLink></li>
                                <li><RouterLink to="/services/red-team" onClick={() => setIsOpen(false)} className="block py-1 hover:text-red-400">Red Team & Pentest</RouterLink></li>
                                <li><RouterLink to="/services/ctf-vendor" onClick={() => setIsOpen(false)} className="block py-1 hover:text-red-400">CTF Organizer</RouterLink></li>
                                <li><RouterLink to="/services/bug-bounty" onClick={() => setIsOpen(false)} className="block py-1 hover:text-red-400">Bug Bounty as a Service</RouterLink></li>
                            </ul>
                        </li>
                        <li>
                            <span onClick={() => { setIsOpen(false); scrollTo('testimonials'); }} className="cursor-pointer hover:text-red-400">
                                Testimonials
                            </span>
                        </li>
                        <li>
                            <span onClick={() => { setIsOpen(false); scrollTo('contact'); }} className="cursor-pointer hover:text-red-400">
                                Contact
                            </span>
                        </li>
                        <li>
                            <a href="https://akademi.siberlab.id/" className="block py-1 hover:text-red-400">Akademi Siber</a>
                        </li>
                        <li>
                            <RouterLink to="/services/ctf-vendor" onClick={() => setIsOpen(false)} className="btn-primary mt-3 w-full justify-center">
                                Run a CTF <FiArrowRight />
                            </RouterLink>
                        </li>
                    </ul>
                </div>
            )}
        </nav>
    );
};

const NavItem = ({ onClick, children }) => (
    <li>
        <span onClick={onClick} className="cursor-pointer px-3 py-2 rounded-md hover:bg-white/5 hover:text-red-400 transition inline-block">
            {children}
        </span>
    </li>
);

const DropdownItem = ({ to, icon, label, desc, external }) => {
    const inner = (
        <div className="flex items-start gap-3 px-4 py-3 hover:bg-red-500/10 transition">
            <div className="w-9 h-9 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center justify-center text-red-400 flex-shrink-0">
                {icon}
            </div>
            <div>
                <div className="font-semibold">{label}</div>
                <div className="text-xs text-gray-400">{desc}</div>
            </div>
        </div>
    );
    return (
        <li>
            {external ? (
                <a href={to} target="_blank" rel="noreferrer">{inner}</a>
            ) : (
                <RouterLink to={to}>{inner}</RouterLink>
            )}
        </li>
    );
};

export default Navbar;
